import foliageLexicon from './foliage-lexicon.json';

export interface AdvancedSearchAttachment {
  id: string;
  file: File;
  previewUrl: string;
}

export const ADVANCED_SEARCH_BASELINE_COUNT = 12;
export const ADVANCED_SEARCH_MAX_COUNT = 24;
export const ADVANCED_SEARCH_GENERATION_CONCURRENCY = 4;
export const ADVANCED_SEARCH_SHEET_BATCH_SIZE = 12;
export const ADVANCED_SEARCH_SHEET_BATCH_DELAY_MS = 8000;
export const ADVANCED_SEARCH_SHEET_BATCH_RETRY_COUNT = 2;
export const ADVANCED_SEARCH_SHEET_BATCH_RETRY_DELAY_MS = 8000;
export const ADVANCED_SEARCH_CUSTOM_MIN_COUNT = 1;
export const ADVANCED_SEARCH_CUSTOM_MAX_COUNT = 30;
export const ADVANCED_SEARCH_CUSTOM_DEFAULT_COUNT = 4;
export const ADVANCED_SEARCH_CUSTOM_SYSTEM_PROMPT_MAX_LENGTH = 2000;
export const ADVANCED_SEARCH_CUSTOM_TEMPERATURE_MIN = 0;
export const ADVANCED_SEARCH_CUSTOM_TEMPERATURE_MAX = 1;
export const ADVANCED_SEARCH_CUSTOM_TEMPERATURE_STEP = 0.05;
export const ADVANCED_SEARCH_CUSTOM_PORTRAIT_TEMPERATURE_DEFAULT = 0.2;
export const ADVANCED_SEARCH_CUSTOM_LANDSCAPE_TEMPERATURE_DEFAULT = 0.2;

export type AdvancedSearchGenerationMode = 'sheet-pro' | 'single-pro' | 'custom';
export type AdvancedSearchImageModel = 'flash' | 'pro';
export type AdvancedSearchPlannerModel = 'flash' | 'pro';
export type AdvancedSearchRenderingModel = AdvancedSearchImageModel;

export const ADVANCED_SEARCH_DEFAULT_GENERATION_MODE: AdvancedSearchGenerationMode = 'sheet-pro';
export const ADVANCED_SEARCH_DEFAULT_IMAGE_MODEL: AdvancedSearchImageModel = 'flash';
export const ADVANCED_SEARCH_DEFAULT_PLANNER_MODEL: AdvancedSearchPlannerModel = 'flash';
export const ADVANCED_SEARCH_DEFAULT_RENDERING_MODEL: AdvancedSearchRenderingModel = ADVANCED_SEARCH_DEFAULT_IMAGE_MODEL;

export interface AdvancedSearchGenerationModeOption {
  value: AdvancedSearchGenerationMode;
  title: string;
  description: string;
  deprecatedLabel?: string;
}

export interface AdvancedSearchImageModelOption {
  value: AdvancedSearchImageModel;
  title: string;
  description: string;
}

export interface AdvancedSearchRenderingModelOption {
  value: AdvancedSearchRenderingModel;
  title: string;
  description: string;
}

export interface AdvancedSearchPlannerModelOption {
  value: AdvancedSearchPlannerModel;
  title: string;
  description: string;
}

export const ADVANCED_SEARCH_GENERATION_MODE_OPTIONS: ReadonlyArray<AdvancedSearchGenerationModeOption> = [
  {
    value: 'sheet-pro',
    title: 'Economy Plus',
    description: 'Higher quality Pro batch generation',
    deprecatedLabel: 'DEPRECATED',
  },
  {
    value: 'single-pro',
    title: 'Legacy Pro',
    description: 'Portrait thumbs plus 2K landscape detail renders',
    deprecatedLabel: 'DEPRECATED',
  },
  {
    value: 'custom',
    title: 'Custom',
    description: 'Description-first portrait+landscape pair generation',
  },
];

export const getAdvancedSearchGenerationModeLabel = (mode: AdvancedSearchGenerationMode): string => {
  const option = ADVANCED_SEARCH_GENERATION_MODE_OPTIONS.find((item) => item.value === mode);
  return option?.title || 'Economy Plus';
};

export const ADVANCED_SEARCH_IMAGE_MODEL_OPTIONS: ReadonlyArray<AdvancedSearchImageModelOption> = [
  {
    value: 'flash',
    title: 'Flash',
    description: 'Faster and cheaper model.',
  },
  {
    value: 'pro',
    title: 'Pro',
    description: 'Higher quality model.',
  },
];

export const getAdvancedSearchImageModelLabel = (model: AdvancedSearchImageModel): string => {
  const option = ADVANCED_SEARCH_IMAGE_MODEL_OPTIONS.find((item) => item.value === model);
  return option?.title || 'Flash';
};

export const ADVANCED_SEARCH_RENDERING_MODEL_OPTIONS: ReadonlyArray<AdvancedSearchRenderingModelOption> = [
  {
    value: 'flash',
    title: 'Flash',
    description: 'Faster and cheaper rendering model.',
  },
  {
    value: 'pro',
    title: 'Pro',
    description: 'Higher quality rendering model.',
  },
];

export const getAdvancedSearchRenderingModelLabel = (model: AdvancedSearchRenderingModel): string => {
  const option = ADVANCED_SEARCH_RENDERING_MODEL_OPTIONS.find((item) => item.value === model);
  return option?.title || 'Flash';
};

export const ADVANCED_SEARCH_PLANNER_MODEL_OPTIONS: ReadonlyArray<AdvancedSearchPlannerModelOption> = [
  {
    value: 'flash',
    title: 'Flash',
    description: 'Faster and cheaper planner model.',
  },
  {
    value: 'pro',
    title: 'Pro',
    description: 'Higher quality planner model with fallback chain.',
  },
];

export const getAdvancedSearchPlannerModelLabel = (model: AdvancedSearchPlannerModel): string => {
  const option = ADVANCED_SEARCH_PLANNER_MODEL_OPTIONS.find((item) => item.value === model);
  return option?.title || 'Flash';
};

export const normalizeAdvancedSearchGenerationMode = (
  value: string | null | undefined,
): AdvancedSearchGenerationMode | null => {
  if (value === 'sheet-pro' || value === 'single-pro' || value === 'custom') {
    return value;
  }

  return null;
};

export const normalizeAdvancedSearchImageModel = (
  value: string | null | undefined,
): AdvancedSearchImageModel | null => {
  if (value === 'flash' || value === 'pro') {
    return value;
  }

  return null;
};

export const normalizeAdvancedSearchRenderingModel = (
  value: string | null | undefined,
): AdvancedSearchRenderingModel | null => {
  return normalizeAdvancedSearchImageModel(value);
};

export const normalizeAdvancedSearchPlannerModel = (
  value: string | null | undefined,
): AdvancedSearchPlannerModel | null => {
  if (value === 'flash' || value === 'pro') {
    return value;
  }

  return null;
};

// ── Render profile (foliage detection / override) ───────────────────────────
// "auto"    → run the shared keyword detector (default)
// "foliage" → force the foliage render path
// "generic" → force the standard (non-foliage) render path
export type AdvancedSearchRenderProfile = 'auto' | 'foliage' | 'generic';

export const ADVANCED_SEARCH_DEFAULT_RENDER_PROFILE: AdvancedSearchRenderProfile = 'auto';

export interface AdvancedSearchRenderProfileOption {
  value: AdvancedSearchRenderProfile;
  label: string;
  description: string;
}

export const ADVANCED_SEARCH_RENDER_PROFILE_OPTIONS: ReadonlyArray<AdvancedSearchRenderProfileOption> = [
  { value: 'auto', label: 'Auto', description: 'Detect vegetation automatically from the prompt.' },
  { value: 'generic', label: 'Generic', description: 'Force the standard asset render (no foliage rules).' },
  { value: 'foliage', label: 'Vegetation', description: 'Force the foliage render (leaf clusters, rim light, subsurface).' },
];

export const getAdvancedSearchRenderProfileLabel = (
  profile: AdvancedSearchRenderProfile | null | undefined,
): string => {
  const found = ADVANCED_SEARCH_RENDER_PROFILE_OPTIONS.find((option) => option.value === profile);
  return found ? found.label : 'Auto';
};

export const normalizeAdvancedSearchRenderProfile = (
  value: string | null | undefined,
): AdvancedSearchRenderProfile => {
  return value === 'foliage' || value === 'generic' ? value : 'auto';
};

// Per-asset landmark auto-grounding mode (mirrors the render-profile pattern):
// 'auto' = detect real-world landmarks at generation time and ground them,
// 'force' = ground even with weak references, 'off' = never auto-ground.
export type AdvancedSearchLandmarkMode = 'auto' | 'force' | 'off';
export type AdvancedSearchLandmarkGroundingSource = 'auto' | 'manual';
export type AdvancedSearchLandmarkDetectionStatus = 'landmark' | 'not_landmark' | 'weak' | 'error';

export const normalizeAdvancedSearchLandmarkMode = (
  value: string | null | undefined,
): AdvancedSearchLandmarkMode => {
  return value === 'force' || value === 'off' ? value : 'auto';
};

export interface AdvancedSearchFoliageAssetInput {
  title?: string | null;
  imagePrompt?: string | null;
  normalizedPrompt?: string | null;
}

// Detection lexicon is shared verbatim with the Cloud Function detector through
// the canonical foliage-lexicon.json (mirrored at functions/.../foliage-lexicon.json;
// byte-equality enforced by foliage-lexicon.parity.test.js). Keep this algorithm
// in lock-step with isAdvancedSearchFoliageAsset in functions/.../service.js.
const FOLIAGE_LEXICON = foliageLexicon as unknown as {
  strongWords: string[];
  species: string[];
  plantIndicators: string[];
  furnitureContext: string[];
  stopwordPhrases: string[];
};

export const isAdvancedSearchFoliageAsset = (
  asset: AdvancedSearchFoliageAssetInput | null | undefined,
): boolean => {
  const title = asset?.title || '';
  const imagePrompt = asset?.imagePrompt || '';
  const normalizedPrompt = asset?.normalizedPrompt || '';
  const haystack = `${title} ${imagePrompt} ${normalizedPrompt}`.toLowerCase();
  if (!haystack.trim()) {
    return false;
  }
  if (FOLIAGE_LEXICON.stopwordPhrases.some((phrase) => haystack.includes(phrase))) {
    return false;
  }
  const hasWord = (word: string): boolean => new RegExp(`\\b${word}\\b`).test(haystack);

  if (FOLIAGE_LEXICON.strongWords.some(hasWord)) {
    return true;
  }
  if (hasWord('plant') || hasWord('plants')) {
    return true;
  }
  if (FOLIAGE_LEXICON.furnitureContext.some(hasWord)) {
    return false;
  }
  if (FOLIAGE_LEXICON.species.some((species) => hasWord(`${species}s`))) {
    return true;
  }
  if (FOLIAGE_LEXICON.species.some(hasWord) && FOLIAGE_LEXICON.plantIndicators.some(hasWord)) {
    return true;
  }
  return false;
};

// Effective foliage flag given an optional admin override. Mirrors
// resolveAdvancedSearchFoliage in functions/.../service.js.
export const resolveAdvancedSearchFoliage = (
  renderProfile: string | null | undefined,
  asset: AdvancedSearchFoliageAssetInput | null | undefined,
): boolean => {
  const profile = normalizeAdvancedSearchRenderProfile(renderProfile);
  if (profile === 'foliage') {
    return true;
  }
  if (profile === 'generic') {
    return false;
  }
  return isAdvancedSearchFoliageAsset(asset);
};

// The render profile that will actually be applied, given an override + the
// asset text. Lets the UI truthfully label what the CF renders.
export const resolveAdvancedSearchEffectiveRenderProfile = (
  renderProfile: string | null | undefined,
  asset: AdvancedSearchFoliageAssetInput | null | undefined,
): 'foliage' | 'generic' => {
  return resolveAdvancedSearchFoliage(renderProfile, asset) ? 'foliage' : 'generic';
};

export interface AdvancedSearchDraft {
  prompt: string;
  attachments: AdvancedSearchAttachment[];
}

export type AdvancedSearchMode = 'scene-kit' | 'asset-variations';

export const ADVANCED_SEARCH_MODE_LABELS: Record<AdvancedSearchMode, string> = {
  'scene-kit': 'Scene Breakdown',
  'asset-variations': 'Asset Variations',
};

export interface AdvancedSearchPlannerAsset {
  id: string;
  title: string;
  imagePrompt: string;
  order: number;
}

export interface AdvancedSearchPlannerRequest {
  prompt: string;
  baselineCount?: number;
  maxCount?: number;
  generationMode?: AdvancedSearchGenerationMode;
  plannerModel?: AdvancedSearchPlannerModel;
  plannerTemperature?: number;
}

export interface AdvancedSearchPlannerMeta {
  requestedModel: AdvancedSearchPlannerModel;
  usedModel: AdvancedSearchPlannerModel;
  usedModelId: string;
  fallbackApplied: boolean;
  fallbackChain?: string[];
}

export interface AdvancedSearchPlannerResponse {
  searchMode: AdvancedSearchMode;
  normalizedPrompt: string;
  recommendedCount: number;
  assets: AdvancedSearchPlannerAsset[];
  plannerMeta?: AdvancedSearchPlannerMeta;
}

export type AdvancedSearchPreviewStatus = 'pending' | 'loading' | 'success' | 'error';

export interface AdvancedSearchPreviewCard extends AdvancedSearchPlannerAsset {
  status: AdvancedSearchPreviewStatus;
  imageDataUrl?: string;
  thumbnailImageDataUrl?: string;
  viewerImageUrl?: string;
  viewerImageDataUrl?: string;
  pairBatchImageUrl?: string;
  pairPortraitImageUrl?: string;
  pairLandscapeImageUrl?: string;
  pairBatchImageDataUrl?: string;
  pairPortraitImageDataUrl?: string;
  pairLandscapeImageDataUrl?: string;
  pairMeta?: AdvancedSearchPairMeta;
  promptUsed?: string;
  viewerPromptUsed?: string;
  source?: AdvancedSearchPreviewSource;
  generationMode?: AdvancedSearchGenerationMode;
  normalizedPrompt?: string;
  searchMode?: AdvancedSearchMode;
  errorMessage?: string;
  errorCode?: string;
}

export type AdvancedSearchUiState = 'idle' | 'planning' | 'generating' | 'success' | 'error';

export interface GenerateAdvancedSearchAssetRequest {
  assetId: string;
  title: string;
  imagePrompt: string;
  normalizedPrompt: string;
  searchMode: AdvancedSearchMode;
  generationMode?: 'single-pro';
}

export interface GenerateAdvancedSearchAssetSheetAssetRequest {
  assetId: string;
  title: string;
  imagePrompt: string;
}

export interface GenerateAdvancedSearchAssetSheetRequest {
  normalizedPrompt: string;
  searchMode: AdvancedSearchMode;
  assets: GenerateAdvancedSearchAssetSheetAssetRequest[];
}

export type AdvancedSearchPreviewSource = 'single' | 'sheet' | 'single-fallback' | 'custom';

export interface GenerateAdvancedSearchAssetResponse {
  assetId: string;
  title: string;
  imageDataUrl: string;
  thumbnailImageDataUrl: string;
  viewerImageDataUrl: string;
  promptUsed: string;
  viewerPromptUsed?: string;
  source: AdvancedSearchPreviewSource;
  generationMode?: AdvancedSearchGenerationMode;
}

export interface GenerateAdvancedSearchAssetSheetResponse {
  results: GenerateAdvancedSearchAssetResponse[];
  sheetPromptUsed: string;
  fallbackCount: number;
}

export interface GenerateAdvancedSearchAssetLandscapeRequest {
  assetId: string;
  title: string;
  imagePrompt: string;
  normalizedPrompt: string;
  searchMode: AdvancedSearchMode;
  portraitImageDataUrl: string;
}

export interface GenerateAdvancedSearchAssetLandscapeResponse {
  assetId: string;
  viewerImageDataUrl: string;
  viewerPromptUsed: string;
}

export interface GenerateAdvancedSearchAssetLandscapeReplacementRequest {
  assetId: string;
  title: string;
  imagePrompt: string;
  normalizedPrompt: string;
  searchMode: AdvancedSearchMode;
  portraitUrl?: string;
  portraitStoragePath?: string;
  bucket?: string;
  imageModel?: AdvancedSearchImageModel;
  renderProfile?: AdvancedSearchRenderProfile;
  landscapeTemperature?: number;
  fullSizeLandscapePreview?: boolean;
  landscapePromptOverride?: string;
}

export interface GenerateAdvancedSearchAssetLandscapeReplacementResponse {
  assetId: string;
  landscapeImageUrl: string;
  batchImageUrl: string;
  viewerPromptUsed: string;
  // Minimal provenance so the admin can refresh generationMeta.renderProfile after
  // a landscape-only regeneration (the full pairMeta is not produced on this path).
  pairMeta?: {
    renderProfileApplied?: 'foliage' | 'generic';
  };
  storage: {
    generationId: string;
    bucket: string;
    images: {
      landscape: AdvancedSearchStoredImage;
      batch: AdvancedSearchStoredImage;
    };
  };
}

export interface AdvancedSearchStoredImage {
  url: string;
  storagePath: string;
  byteSize: number;
  width?: number;
  height?: number;
  contentType?: string;
}

export interface AdvancedSearchPairStorageMeta {
  generationId: string;
  bucket: string;
  prefix: string;
  publicUrlMode: 'firebase-token';
  contentType: 'image/jpeg';
  quality: number;
  images: {
    portrait: AdvancedSearchStoredImage;
    landscape: AdvancedSearchStoredImage;
    batch: AdvancedSearchStoredImage;
  };
}

export interface AdvancedSearchPairMeta {
  modelId: string;
  // The render path the Cloud Function actually applied for this generation,
  // resolved from the renderProfile override + the shared foliage detector.
  renderProfileApplied?: 'foliage' | 'generic';
  landmarkApplied?: boolean;
  landmarkGroundingId?: string;
  landmarkSource?: AdvancedSearchLandmarkGroundingSource;
  landmarkDetectionStatus?: AdvancedSearchLandmarkDetectionStatus;
  landmarkDetectionFailureCode?: string;
  portraitImageSizeRequested: string;
  landscapeImageSizeRequested: string;
  portraitImageSizeUsed: string;
  landscapeImageSizeUsed: string;
  portraitTemperatureUsed?: number;
  landscapeTemperatureUsed?: number;
  fullSizeLandscapePreviewRequested?: boolean;
  fullSizeLandscapePreviewApplied?: boolean;
  landscapeStorageMode?: 'conformed-jpeg' | 'full-size-original' | 'full-size-debanded';
  landscapeStorageMimeType?: string;
  landscapeStorageExtension?: string;
  debandAttempted?: boolean;
  debandApplied?: boolean;
  debandMethod?: 'ffmpeg-deband';
  debandParams?: string;
  debandDurationMs?: number;
  fallbackApplied: boolean;
  layout: {
    type: 'native-proportions-side-by-side' | 'height-normalized-side-by-side';
    targetHeight?: number;
    canvas: {
      width: number;
      height: number;
    };
    left: {
      variant: 'portrait';
      width: number;
      height: number;
      top: number;
      left: number;
    };
    right: {
      variant: 'landscape';
      width: number;
      height: number;
      top: number;
      left: number;
    };
  };
  storage?: AdvancedSearchPairStorageMeta;
}

export type GenerateAdvancedSearchAssetPairBatchMeta = AdvancedSearchPairMeta;

export interface GenerateAdvancedSearchAssetPairBatchRequest {
  assetId: string;
  title: string;
  imagePrompt: string;
  normalizedPrompt: string;
  searchMode: AdvancedSearchMode;
  thumbnailImageDataUrl?: string;
  imageModel?: AdvancedSearchImageModel;
  // Render-profile override (auto/foliage/generic). Omitted or 'auto' = let the
  // Cloud Function auto-detect vegetation from the prompt.
  renderProfile?: AdvancedSearchRenderProfile;
  // Optional confirmed landmark grounding id. When present, the Cloud Function
  // loads reference photos + a factual description and forces the pro image model.
  landmarkGroundingId?: string;
  // Durable source of an existing grounding link. Legacy callers may omit it.
  landmarkGroundingSource?: AdvancedSearchLandmarkGroundingSource;
  // Auto-grounding mode when no grounding id is attached (default 'auto').
  landmarkMode?: AdvancedSearchLandmarkMode;
  systemPrompt?: string;
  portraitTemperature?: number;
  landscapeTemperature?: number;
  fullSizeLandscapePreview?: boolean;
  // Optional scene-group context. When present, the Cloud Function mirrors the
  // rendered asset into the Spanner-backed admin DB (via the Go backend) after a
  // successful render. Only the admin generation flow sets these.
  sceneGroupId?: string;
  sceneQueryId?: string;
  itemIndex?: number;
  itemLabel?: string;
}

export interface GenerateAdvancedSearchAssetPairBatchResponse {
  assetId: string;
  batchImageUrl: string;
  portraitImageUrl: string;
  landscapeImageUrl: string;
  viewerImageUrl: string;
  batchImageDataUrl?: string;
  portraitImageDataUrl?: string;
  landscapeImageDataUrl?: string;
  viewerImageDataUrl?: string;
  promptUsed: string;
  viewerPromptUsed: string;
  pairMeta: AdvancedSearchPairMeta;
}

export interface DownloadAdvancedSearchAssetPairZipRequest {
  assetId?: string;
  itemLabel?: string;
  bucket?: string;
  portraitStoragePath?: string;
  portraitUrl?: string;
}

// --- Landmark grounding ("our own Perplexity" for real buildings/sculptures) ---

export interface LandmarkGroundingClassification {
  isRealWorldLandmark: boolean;
  canonicalQuery: string;
  canonicalQueryLocal: string;
  displayName: string;
  landmarkKind: string;
}

export interface LandmarkGroundingCandidate {
  title: string;
  sourceUrl: string | null;
  fullUrl: string;
  thumbUrl: string;
  width: number | null;
  height: number | null;
  license: string | null;
  artist: string | null;
  viewAngle: string | null;
  quality: number;
  isFullExteriorView: boolean;
  recommended: boolean;
}

export interface PrepareLandmarkGroundingRequest {
  title: string;
  imagePrompt?: string;
}

export interface PrepareLandmarkGroundingResponse {
  classification: LandmarkGroundingClassification;
  candidates: LandmarkGroundingCandidate[];
  description: string;
  wikipediaExtract: string | null;
}

export interface ConfirmLandmarkGroundingPhoto {
  sourceUrl?: string;
  fullUrl?: string;
  license?: string | null;
  width?: number | null;
  height?: number | null;
  // Manual upload (fallback path) instead of a Commons photo.
  dataUrl?: string;
}

export interface ResolveLandmarkReferenceUrlsRequest {
  urls: string[];
}

export interface ResolvedReferenceUrl {
  inputUrl: string;
  ok: boolean;
  imageUrl: string | null;
  mimeType: string | null;
  width: number | null;
  height: number | null;
  byteSize: number | null;
  warning: string | null;
  error: string | null;
}

export interface ResolveLandmarkReferenceUrlsResponse {
  results: ResolvedReferenceUrl[];
}

// --- Batch references (recognise a dropped batch of reference photos) ---------

export type ReferenceBatchIssueCode =
  | 'people_in_frame'
  | 'vehicles_in_frame'
  | 'multiple_subjects'
  | 'cropped_subject'
  | 'interior_only'
  | 'detail_shot'
  | 'heavy_occlusion'
  | 'low_resolution'
  | 'watermark_or_text'
  | 'not_an_object';

export interface ReferenceBatchPhotoInput {
  // Stable per-photo id chosen by the browser; every result is matched back by it.
  clientId: string;
  // Downscaled JPEG preview for a local file. Mutually exclusive with imageUrl.
  dataUrl?: string;
  // External link — fetched and downscaled server-side, because a cross-origin canvas
  // cannot be read back in the browser.
  imageUrl?: string;
  fileName?: string;
}

export interface RecognizeReferenceBatchRequest {
  groupTitle?: string;
  groupDescription?: string;
  queryTitle?: string;
  querySubtitle?: string;
  queryPrompt?: string;
  // Labels already used in this query: naming style to match, names not to reuse.
  existingLabels?: string[];
  photos: ReferenceBatchPhotoInput[];
}

export interface ReferenceBatchResult {
  clientId: string;
  ok: boolean;
  error: string | null;
  // Empty when the model was not confident enough — the panel then requires a human name
  // and offers suggestedLabel with one click.
  label: string;
  suggestedLabel: string;
  lowConfidence: boolean;
  kind: 'reference' | 'landmark';
  landmarkName: string;
  canonicalQuery: string;
  landmarkKind: string;
  subjectKind: string;
  description: string;
  issues: Array<{ code: ReferenceBatchIssueCode; note: string }>;
  suggestedInstructions: string;
  confidence: number;
  duplicateOfClientId: string | null;
}

export interface RecognizeReferenceBatchResponse {
  results: ReferenceBatchResult[];
  styleProfile: string;
  rejected: Array<{ clientId: string; error: string }>;
  meta: {
    modelId: string;
    photoCount: number;
    thinkingTokens: number;
    attempts: number;
    tokens?: unknown;
  };
}

export interface ConfirmLandmarkGroundingRequest {
  landmarkName: string;
  canonicalQuery: string;
  landmarkKind: string;
  description: string;
  // Artist directives for the model ("сделать цветным", "не менять конструкцию").
  // Stored beside the factual description and restated at the top of the render
  // prompt with priority over the description and the photos. Empty keeps what is
  // already stored (same rule as description on append).
  instructions?: string;
  wikipediaExtract?: string | null;
  selectedPhotos: ConfirmLandmarkGroundingPhoto[];
  // Batch references. 'reference' derives the document id from the photo bytes instead of
  // from the (often generic) name, so two unrelated assets cannot share one document.
  groundingScope?: 'landmark' | 'reference';
  // Explicit target document; required by photoMode 'append'.
  groundingId?: string;
  // 'append' adds photos to an existing grounding instead of replacing its photo array.
  photoMode?: 'replace' | 'append';
}

export interface ConfirmLandmarkGroundingResponse {
  groundingId: string;
  grounding: {
    id: string;
    landmarkName: string;
    canonicalQuery: string;
    landmarkKind: string;
    description: string;
    photos: Array<{storagePath: string; bucket: string; url: string; sourceUrl: string | null; license: string | null}>;
    groundingScope?: 'landmark' | 'reference';
    status: string;
  };
}
